import {
  isExpiredWorkspaceTrialSubscription,
  isWorkspaceSubscriptionTrial,
  isWorkspaceSubscriptionExpired,
} from '@/contexts/AuthContext/workspaceSubscriptionUtils';

type Subscription = Parameters<typeof isWorkspaceSubscriptionTrial>[0]
  & Parameters<typeof isExpiredWorkspaceTrialSubscription>[0];

export type WorkspaceSubscriptionDisplayState = 'trial' | 'expired-trial' | 'expired-paid' | 'not-trial';

/** Current workspace facts win over billing display state (which may lag a switch).
 * Billing semantic flags are only a fallback when workspace facts are unavailable.
 * Never infer trial eligibility from translated CTA text.
 */
export const resolveWorkspaceSubscriptionDisplayState = (
  subscription: Subscription,
  billing: { isTrial: boolean; status: string },
): WorkspaceSubscriptionDisplayState => {
  if (subscription && (subscription.status || subscription.is_in_trial || subscription.trial_end_at)) {
    // Scheduled cancellation retains access until the paid period actually ends.
    const status = subscription.status?.toLowerCase() || '';
    const hasRemainingPaidPeriod = ['canceled', 'pending_cancellation'].includes(status)
      && Number(new Date(subscription.current_period_end || '')) > Date.now();
    if (hasRemainingPaidPeriod && !isWorkspaceSubscriptionTrial(subscription)) return 'not-trial';
    if (isExpiredWorkspaceTrialSubscription(subscription)) return 'expired-trial';
    if (isWorkspaceSubscriptionTrial(subscription)) return 'trial';
    if (['trial', 'trialing'].includes(status)) {
      return Number(new Date(subscription.trial_end_at || '')) <= Date.now() ? 'expired-trial' : 'trial';
    }
    if (isWorkspaceSubscriptionExpired(subscription)) return 'expired-paid';
    return 'not-trial';
  }
  if (billing.isTrial && billing.status === 'expired') return 'expired-trial';
  if (billing.isTrial || billing.status === 'trial') return 'trial';
  if (billing.status === 'expired') return 'expired-paid';
  return 'not-trial';
};
